import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';

import { canSee, useSession } from '@/features/auth';

import { SETTINGS_GROUPS } from '../model/sections';

import styles from './Settings.module.css';

/**
 * Ustawienia — **powłoka zorientowana na kategorie i sekcje** (spec sek. 10).
 *
 * ⭐ Ekran jest zaprojektowany pod **rozbudowę**, bo to jest miejsce, do którego trafia
 * wszystko, co w produkcie się konfiguruje: dziś organizacja i konto, jutro słownik typów,
 * progi, integracje, branding. Dlatego układ to **spis sekcji po lewej + treść po prawej**,
 * a nie zakładki w poziomie — poziome zakładki kończą się na siedmiu i zaczynają zawijać.
 *
 * **Dołożenie sekcji = jeden wpis w `SETTINGS_GROUPS` i jedna trasa.** Nic poza tym:
 * nagłówek, zaznaczenie i widoczność wynikają z tej tablicy.
 *
 * ⚠ Sekcje admina są przed innymi **ukrywane, nie blokowane**. Kontrolę trzyma serwer
 * (`AdminOnly` na `/api/users` i na wszystkich zmianach) — tu chodzi o to, żeby nie
 * pokazywać drzwi, które i tak są zamknięte.
 */

export function SettingsScreen() {
  const { t } = useTranslation();
  const { data: user } = useSession();

  return (
    <div className={styles['screen']}>
      {/* Druga nawigacja na stronie — czytnik ekranu rozróżnia je nazwą. */}
      <nav className={styles['rail']} aria-label={t('app.nav.settings')}>
        {SETTINGS_GROUPS.map((group) => {
          // `canSee` dokłada admina samo — patrz komentarz przy `SettingsSection.roles`.
          const visible = group.sections.filter((section) => canSee(user, section.roles));

          if (visible.length === 0) {
            return null;
          }

          return (
            <div key={group.labelKey} className={styles['group']}>
              <span className={styles['groupLabel']}>{t(group.labelKey)}</span>

              {visible.map((section) => (
                <NavLink
                  key={section.path}
                  to={section.path}
                  className={({ isActive }) =>
                    `${styles['sectionLink']} ${isActive ? styles['sectionLinkActive'] : ''}`
                  }
                >
                  {t(section.labelKey)}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className={styles['content']}>
        <Outlet />
      </div>
    </div>
  );
}

/**
 * Nagłówek sekcji — tytuł plus jedno zdanie o tym, **co tu można zrobić i czego nie**.
 *
 * Zdanie nie jest ozdobą: ustawienia tego produktu mają nieoczywiste ograniczenia
 * (brak maili, aktywacja ręczna, jeden link na firmę) i bez wytłumaczenia wyglądają
 * jak brakujące funkcje.
 */
export function SectionHead({ titleKey, leadKey }: { titleKey: string; leadKey: string }) {
  const { t } = useTranslation();

  return (
    <header className={styles['head']}>
      <h1 className={styles['title']}>{t(titleKey)}</h1>
      <p className={styles['lead']}>{t(leadKey)}</p>
    </header>
  );
}
